// src/features/SegmentTagsModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Tag, Input, Space, Button, message, Empty, Divider, Popconfirm } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { apiRequests } from "src/shared/api/api";

/**
 * Modal для просмотра/добавления/удаления тегов сегмента.
 *
 * Props:
 * - visible: boolean
 * - onClose: () => void
 * - segmentId: number | null
 */
const SegmentTagsModal = ({ visible, onClose, segmentId }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [adding, setAdding] = useState(false);

  const loadTags = async () => {
    if (!segmentId) {
      setTags([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequests.segments.getTags(segmentId);
      setTags(res.data || []);
    } catch (err) {
      console.error("Ошибка загрузки тегов:", err);
      message.error("Не удалось загрузить теги");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadTags();
      setNewTagName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, segmentId]);

  const handleAdd = async () => {
    const name = (newTagName || "").trim();
    if (!name) return message.warn("Введите имя тега");
    if (!segmentId) return message.error("Не выбран сегмент");

    setAdding(true);
    try {
      await apiRequests.segments.addTags(segmentId, [{ name }]);
      message.success("Тег добавлен");
      setNewTagName("");
      await loadTags();
    } catch (err) {
      console.error("Ошибка добавления тега:", err);
      const detail = err?.response?.data?.detail || "Не удалось добавить тег";
      message.error(detail);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (tagName) => {
    if (!segmentId) return message.error("Не выбран сегмент");
    try {
      await apiRequests.segments.removeTags(segmentId, [{ name: tagName }]);
      message.success("Тег удалён");
      await loadTags();
    } catch (err) {
      console.error("Ошибка удаления тега:", err);
      const detail = err?.response?.data?.detail || "Не удалось удалить тег";
      message.error(detail);
    }
  };

  return (
    <Modal
      title={segmentId ? `Теги сегмента #${segmentId}` : "Теги сегмента"}
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <Input
          placeholder="Новое имя тега (например: vip)"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onPressEnter={handleAdd}
          disabled={!segmentId || adding}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={adding}
          disabled={!segmentId}
        >
          Добавить
        </Button>
      </div>

      <Divider />

      <div>
        {loading ? (
          <div>Загрузка...</div>
        ) : tags.length === 0 ? (
          <Empty description="Тегов нет" />
        ) : (
          <Space wrap>
            {tags.map((t) => (
              <Popconfirm
                key={t.id}
                title={`Удалить тег "${t.name}"?`}
                onConfirm={() => handleRemove(t.name)}
                okText="Удалить"
                cancelText="Отмена"
              >
                <Tag
                  key={t.id}
                  style={{ cursor: "pointer" }}
                  color={t.color || undefined}
                >
                  {t.emoji ? `${t.emoji} ` : ""}
                  {t.name}
                </Tag>
              </Popconfirm>
            ))}
          </Space>
        )}
      </div>
    </Modal>
  );
};

export default SegmentTagsModal;
